import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Link } from '@remix-run/react';
import { CommunityChannelIcon } from '@shared/dream-components';
import cx from 'classnames';

import PushPinIcon from '~/components/Icons/PushPinIcon';
import { COMMUNITY_ROUTES } from '~/constants/community';
import type { CommunityMember } from '~/interfaces/community/member';
import type { CommunitySpace } from '~/interfaces/community/space';
import { useShortRelativeDate } from '~/routes/community/_hooks/useShortRelativeDate';

import { Avatar } from '../../ui/Avatar';
import { MemberBadge } from '../../ui/MemberBadge';
import { MemberProfileLink } from '../../ui/MemberProfileLink';

type PostAuthorHeaderProps = {
  author: CommunityMember;
  createdAt: string;
  editedAt?: string | null;
  space?: Pick<CommunitySpace, 'name' | 'emoji' | 'slug'> | null;
  hideSpace?: boolean;
  isPinned?: boolean;
  size?: 'default' | 'compact';
};

export const PostAuthorHeader: FC<PostAuthorHeaderProps> = ({
  author,
  createdAt,
  editedAt,
  space,
  hideSpace = false,
  isPinned = false,
  size = 'default',
}) => {
  const { t } = useTranslation();
  const formatRelativeTime = useShortRelativeDate();
  const displayName = author.name || t('community.join.former_member');
  const showSpace = !hideSpace && Boolean(space?.name);
  const metaTextClass = size === 'compact' ? 'text-black/60' : 'text-black/50';

  return (
    <div className="flex flex-1 items-center gap-4 min-w-0">
      <MemberProfileLink memberId={author.id} aria-label={displayName} className="no-underline">
        <Avatar src={author.avatar_url} alt={displayName} className={size === 'compact' ? 'size-8' : 'size-10'} />
      </MemberProfileLink>

      <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-x-[6px] gap-y-1 min-w-0 flex-1">
        <div className="flex items-center gap-[6px] flex-wrap min-w-0">
          <MemberProfileLink
            memberId={author.id}
            className="font-semibold text-[15px] leading-5 tracking-[-0.15px] text-black whitespace-nowrap"
          >
            {displayName}
          </MemberProfileLink>
          <MemberBadge
            roles={author.roles}
            isBanned={author.computed?.banned === true}
            blockedYou={author.computed?.blocked_you === true}
            badges={author.badges}
          />
          <span className={cx('text-[15px]', metaTextClass)}>&bull;</span>
          <span className={cx('text-[15px] leading-5 tracking-[-0.15px]', metaTextClass, 'whitespace-nowrap')}>
            {formatRelativeTime(createdAt)}
          </span>
          {editedAt && (
            <span
              className="text-[13px] leading-4 text-black/30 italic shrink-0"
              title={formatRelativeTime(editedAt, { withAgo: true })}
            >
              {t('community.comments.edited_label')}
            </span>
          )}
        </div>
        {(showSpace || isPinned) && (
          <div className="flex items-center gap-[6px] min-w-0 shrink-0">
            {showSpace && space && (
              <>
                <span className={cx('hidden md:inline text-[15px]', metaTextClass)}>&bull;</span>
                <CommunityChannelIcon
                  slug={space.slug}
                  emoji={space.emoji}
                  className={cx('size-4', metaTextClass, 'shrink-0')}
                />
                <Link
                  to={COMMUNITY_ROUTES.spaces(space.slug)}
                  onClick={(e) => e.stopPropagation()}
                  className={cx('text-[15px] leading-5 tracking-[-0.15px]', metaTextClass, 'whitespace-nowrap')}
                >
                  {space.name}
                </Link>
              </>
            )}
            {isPinned && (
              <>
                {showSpace ? (
                  <span className={cx('text-[15px]', metaTextClass)}>&bull;</span>
                ) : (
                  <span className={cx('hidden md:inline text-[15px]', metaTextClass)}>&bull;</span>
                )}
                <span className={cx('inline-flex size-5 items-center justify-center shrink-0', metaTextClass)}>
                  <PushPinIcon className="size-3.5" />
                </span>
                <span className="sr-only">{t('community.profile.pinned')}</span>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
